import React, { useState, useEffect } from 'react';
import NoTrafficClientsForm from './NoTrafficClientsForm';
import NoTrafficClientsList from './NoTrafficClientsList';
import NoTrafficClientsHistory from './NoTrafficClientsHistory';
import NoTrafficClientsQuickActionModal from './NoTrafficClientsQuickActionModal';
import { loadNoTrafficClients } from '../api';

const NoTrafficClientsSection = ({ currentUser }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [quickActionRecord, setQuickActionRecord] = useState(null);

  useEffect(() => {
    loadRecords();
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      
      // Pasar las células del usuario si requiere filtrado por células
      const userCells = currentUser && currentUser.role !== 'admin' && currentUser.cells ? currentUser.cells : null;
      const userRole = currentUser ? currentUser.role : null;
      const userEmail = currentUser ? currentUser.email : null;
      console.log('🔍 No Traffic ClientsSection - Cargando con células del usuario:', userCells);
      console.log('🔍 No Traffic ClientsSection - Rol del usuario:', userRole);
      console.log('🔍 No Traffic ClientsSection - Email del usuario:', userEmail);
      
      const recordsData = await loadNoTrafficClients(userCells, userRole, userEmail);
      setRecords(recordsData || []);
    } catch (error) {
      console.error('Error loading no traffic clients:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (recordData) => {
    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const url = editingRecord 
        ? `${API_URL}/no-traffic-clients/${editingRecord.id}`
        : `${API_URL}/no-traffic-clients`;
      
      const method = editingRecord ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...recordData,
          user_name: currentUser?.name || currentUser?.email || 'Sistema'
        }),
      });

      if (response.ok) {
        alert(editingRecord 
          ? 'Registro actualizado exitosamente' 
          : 'Registro creado exitosamente');
        setShowForm(false);
        setEditingRecord(null);
        loadRecords();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar el registro');
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      alert(`Error al guardar: ${error.message}`);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este registro?')) {
      return;
    }

    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/no-traffic-clients/${recordId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('Registro eliminado exitosamente');
        loadRecords();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar el registro');
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      alert(`Error al eliminar: ${error.message}`);
    }
  };

  const handleShowHistory = (record) => {
    setSelectedRecord(record);
    setShowHistory(true);
  };

  const handleQuickAction = (record) => {
    setQuickActionRecord(record);
    setShowQuickAction(true);
  };

  const handleQuickActionSave = async (recordId, actionData) => {
    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/no-traffic-clients/${recordId}/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(actionData),
      });

      if (response.ok) {
        alert('Acción registrada exitosamente');
        setShowQuickAction(false);
        setQuickActionRecord(null);
        
        // Si se cambió el estado, actualizar la lista
        if (actionData.action_type === 'status_changed') {
          loadRecords();
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al registrar la acción');
      }
    } catch (error) {
      console.error('Error al registrar acción:', error);
      throw error; // Re-lanzar para que el modal pueda manejarlo
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        <span className="ml-3 text-lg text-gray-600">Cargando registros...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">📊 Sin Tráfico</h1>
            <p className="text-red-100 mt-2">
              Gestión de clientes sin tráfico en campañas publicitarias
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{records.length}</div>
            <div className="text-red-100">Registros Activos</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-4">
          <button
            onClick={() => {
              setEditingRecord(null);
              setShowForm(true);
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
          >
            <span className="mr-2">➕</span>
            Nuevo Registro
          </button>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Pendiente</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
            <span className="text-sm text-gray-600">En Proceso</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Completado</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Cancelado</span>
          </div>
        </div>
      </div>

      {/* Lista de registros */}
      <NoTrafficClientsList
        records={records}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onShowHistory={handleShowHistory}
        onQuickAction={handleQuickAction}
        currentUser={currentUser}
      />

      {/* Modales */}
      {showForm && (
        <NoTrafficClientsForm
          record={editingRecord}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
          currentUser={currentUser}
        />
      )}

      {showHistory && selectedRecord && (
        <NoTrafficClientsHistory
          recordId={selectedRecord.id}
          onClose={() => {
            setShowHistory(false);
            setSelectedRecord(null);
          }}
        />
      )}

      {showQuickAction && quickActionRecord && (
        <NoTrafficClientsQuickActionModal
          record={quickActionRecord}
          onClose={() => {
            setShowQuickAction(false);
            setQuickActionRecord(null);
          }}
          onSave={handleQuickActionSave}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default NoTrafficClientsSection;
